import { PartyListItemResponse } from 'compliance-api/generated';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { countryOptions } from '@/modules/common/constants/countries.ts';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import PartyFullInfoModal from '@/modules/admin-dashboard/components/contractManagement/PartyFullInfoModal.tsx';
import PartyModal from '@/modules/admin-dashboard/components/contractManagement/PartyModal.tsx';
import {
  useArchivePartyMutation,
  usePartiesListQuery,
  useRestorePartyMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  ModalProviderContext,
  RadioButton,
  SearchField,
  Select,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { DotsHorizontalIcon, FilterLinesGrayIcon, PlusIcon } from 'ui/icons';

type PartySortBy = 'total_value_desc' | 'total_value_asc';

interface PartiesTabProps {
  archive: boolean;
}

const SORT_OPTIONS: { label: string; value: PartySortBy; direction: 'asc' | 'desc' }[] = [
  {
    label: 'Total Value (Highest First)',
    value: 'total_value_desc',
    direction: 'desc',
  },
  {
    label: 'Total Value (Lowest First)',
    value: 'total_value_asc',
    direction: 'asc',
  },
];

const countryDisplayNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const formatCountry = (country?: string) => {
  if (!country) return '-';
  if (country.length !== 2) return country;
  return countryDisplayNames?.of(country.toUpperCase()) || country;
};

export const PartiesTab = ({ archive }: PartiesTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const [params, setParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<PartySortBy>('total_value_desc');
  const { mutateAsync: archiveParty } = useArchivePartyMutation();

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, isError } = usePartiesListQuery({
    page: 1,
    page_size: 30,
    search: searchValue || undefined,
    incorporation_country: country || undefined,
    is_active: !archive,
  });

  const selectedSort = useMemo(
    () => SORT_OPTIONS.find(({ value }) => value === sortBy) || SORT_OPTIONS[0],
    [sortBy],
  );

  const parties = useMemo(() => {
    const items = (data?.pages || []).flatMap(page => page.data);
    return items.sort((a, b) =>
      selectedSort.direction === 'desc'
        ? b.total_value - a.total_value
        : a.total_value - b.total_value,
    );
  }, [data?.pages, selectedSort.direction]);

  const handleSelectSort = useCallback((value: PartySortBy) => {
    setSortBy(value);
  }, []);

  const functionalSortOptions = useMemo<DropdownMenuItem<ReactNode>[]>(
    () =>
      SORT_OPTIONS.map(({ label, value }) => ({
        label: <RadioButton items={[{ label, value }]} value={sortBy} onChange={() => 0} />,
        value,
        onClick: () => handleSelectSort(value),
      })),
    [handleSelectSort, sortBy],
  );

  const handleOpenPartyFullInfoModal = useCallback(
    (partyId?: number) => {
      if (!partyId) return;
      openModal(PartyFullInfoModal, { partyId });
    },
    [openModal],
  );

  const partyIdParam = params.get('party_id');

  useEffect(() => {
    if (!partyIdParam) return;

    const numericPartyId = Number(partyIdParam);
    if (!Number.isFinite(numericPartyId) || numericPartyId <= 0) return;

    openModal(PartyFullInfoModal, { partyId: numericPartyId });
    const nextParams = new URLSearchParams(params);
    nextParams.delete('party_id');
    setParams(nextParams, { replace: true });
  }, [openModal, params, partyIdParam, setParams]);

  const headItems = useMemo<TableHeadColumn[]>(
    () => [
      // {
      //   title: <CustomCheckbox disabled />,
      //   className: 'w-[44px] min-w-[44px]',
      // },
      {
        title: 'Legal name',
      },
      {
        title: 'Incorporation country',
      },
      {
        title: 'Linked Contracts',
      },
      {
        title: '',
      },
    ],
    [],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    return parties.map(party => ({
      cells: [
        ...getPartyCells(party),
        getPartyActionCell({
          party,
          archive,
          onArchive: () =>
            openModal(ConfirmModal, {
              onConfirm: async () => {
                await archiveParty(party.id);
              },
              title: (
                <p>
                  Are you sure you want to archive{' '}
                  <span className="text-blue-700">{party.party_name || '-'}</span>?
                </p>
              ),
            }),
        }),
      ],
      onClick: archive ? undefined : () => handleOpenPartyFullInfoModal(party.id),
    }));
  }, [archive, archiveParty, handleOpenPartyFullInfoModal, openModal, parties]);

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-col gap-3 px-3 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SearchField
            placeholder="Search"
            value={searchValue}
            onChange={setSearchValue}
            isLoading={isFetching && !isLoading}
            allowClear
            className="w-full sm:w-[240px]"
          />
          <Select
            options={countryOptions}
            value={country}
            onChange={setCountry}
            isClearable
            withBorder
            placeholder="Incorporation country"
            className="w-full sm:w-[240px]"
          />
          {isFetching && <Spinner size="sm" variant="primary" className="m-0" />}
        </div>
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <DropdownMenu
            className="w-[250px]"
            containerClassName="w-fit"
            placement="bottom"
            button={
              <div className="flex h-10 w-fit items-center gap-2 rounded px-2 text-sm leading-[22px] text-gray-900 hover:bg-gray-100">
                <img src={FilterLinesGrayIcon} alt="sort" className="h-5 w-5" />
                <span className="text-gray-500">Sort:</span>
                <span className="font-medium">{selectedSort.label}</span>
              </div>
            }
            items={functionalSortOptions}
          />
          <Button className="w-fit gap-2" onClick={() => openModal(PartyModal)}>
            <img src={PlusIcon} alt="" className="h-5 w-5 invert" />
            <span>New party</span>
          </Button>
        </div>
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={false}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoading}
        isError={isError}
        isSearchUsed={!!searchValue || !!country || archive}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

const getPartyCells = (party: PartyListItemResponse): BodyCell[] => [
  // {
  //   cell: <CustomCheckbox disabled />,
  //   className: 'w-[44px] min-w-[44px]',
  // },
  {
    cell: <span className="font-medium text-gray-900">{party.party_name || '-'}</span>,
  },
  {
    cell: <span>{formatCountry(party.incorporation_country)}</span>,
  },
  {
    cell: <span>{party.linked_contracts}</span>,
  },
];

const getPartyActionCell = ({
  party,
  archive,
  onArchive,
}: {
  party: PartyListItemResponse;
  archive: boolean;
  onArchive: () => void;
}): BodyCell => ({
  cell: (
    <div className="flex justify-end" onClick={event => event.stopPropagation()}>
      {archive ? (
        <ArchivedItemAction id={party.id} mutation={useRestorePartyMutation} />
      ) : (
        <DropdownMenu
          autoPlace
          items={[
            {
              label: 'Archive',
              onClick: onArchive,
            },
          ]}
          button={
            <button className="flex items-center justify-center">
              <img src={DotsHorizontalIcon} alt="Actions" className="w-6 h-6 min-w-6 min-h-6" />
            </button>
          }
          className="w-[180px] divide-white"
        />
      )}
    </div>
  ),
});
